import React from 'react';
import { IPrintIncidentReportProps, IPrintIncidentReportData } from './print-incident-reports.types';
import ReporterIcon from 'assets/icons/eiq-reporter.png';
import { Document, Page, Text, View, StyleSheet, Image, Font } from '@react-pdf/renderer';
import { formatCustomDateTime } from 'common/helpers/date.helper';
import WorkSansRegular from '../../../../assets/fonts/WorkSans-Regular.ttf';
import WorkSansBold from '../../../../assets/fonts/WorkSans-Bold.ttf';
import WorkSansMedium from '../../../../assets/fonts/WorkSans-Medium.ttf';
import { formatAddressLabel } from 'common/helpers/address.helper';
import { formatPhone } from 'common/helpers/phone.helper';

const PrintIncidentReport = (props: IPrintIncidentReportProps, ref: any) => {
  Font.registerHyphenationCallback((w) => [w]);
  Font.register({
    family: 'Work Sans',
    fonts: [
      { src: WorkSansRegular, fontWeight: 400 },
      { src: WorkSansBold, fontWeight: 600 },
      { src: WorkSansMedium, fontWeight: 500 },
    ],
  });

  const Header = (data: IPrintIncidentReportData) => {
    return (
      <View fixed style={styles.header}>
        <Image src={props.logo} style={styles.logo} />
        <Text>Incident# {data.caseNumber}</Text>
      </View>
    );
  };

  const Footer = (data: IPrintIncidentReportData) => {
    return (
      <View fixed style={styles.footer}>
        <Image src={ReporterIcon} style={styles.logo} />
      </View>
    );
  };

  const getPage = (data: IPrintIncidentReportData, index: number) => {
    return (
      <Page size={'A4'} style={styles.page} key={index}>
        <Header {...data} />
        <View style={styles.reportingOffcer}>
          <Text>Reporting Officer</Text>
          <View style={styles.reportingOffcerData}>
            <Text style={styles.text}>{data.reportingPerson}</Text>
            <Text style={styles.text}>{formatCustomDateTime(data.incidentDate, true)}</Text>
          </View>
        </View>
        <View style={styles.delimiter}></View>
        <View style={styles.incidentLocation}>
          <Text style={styles.incidentLocationTitle}>Incident Location</Text>
          <View style={styles.row} wrap={false}>
            <View style={styles.data}>
              <Text style={[styles.dataTitle, styles.text]}>House Number</Text>
              <Text style={[styles.dataValue, styles.text]}>{data.address.streetNumber}</Text>
            </View>
            <View style={styles.data}>
              <Text style={[styles.dataTitle, styles.text]}>Street Name</Text>
              <Text style={[styles.dataValue, styles.text]}>{data.address.street.name}</Text>
            </View>
            <View style={styles.data}>
              <Text style={[styles.dataTitle, styles.text]}>Address Details</Text>
              <Text style={[styles.dataValue, styles.text]}>{formatAddressLabel(data.address, { hideStreet: true })}</Text>
            </View>
          </View>
          <View style={styles.row} wrap={false}>
            <View style={styles.data}>
              <Text style={[styles.dataTitle, styles.text]}>Community</Text>
              <Text style={[styles.dataValue, styles.text]}>{data.address.street.community?.name}</Text>
            </View>
            <View style={styles.data}>
              <Text style={[styles.dataTitle, styles.text]}>Gate</Text>
              <Text style={[styles.dataValue, styles.text]}>{data.gate}</Text>
            </View>
          </View>
        </View>
        <View style={styles.delimiter}></View>
        <View style={styles.incidentLocation}>
          <Text style={styles.incidentLocationTitle}>Incident Details</Text>
          <View style={styles.row} wrap={false}>
            <View style={styles.data}>
              <Text style={[styles.dataTitle, styles.text]}>Classification</Text>
              <Text style={[styles.dataValue, styles.text]}>{data.category}</Text>
            </View>
            <View style={styles.data}>
              <Text style={[styles.dataTitle, styles.text]}>Sub-Incident classification</Text>
              <Text style={[styles.dataValue, styles.text]}>{data.subCategory}</Text>
            </View>
            <View style={styles.data}>
              <Text style={[styles.dataTitle, styles.text]}>Weather Condition</Text>
              <Text style={[styles.dataValue, styles.text]}>{data.weatherCondition}</Text>
            </View>
          </View>
          <View style={styles.row} wrap={false}>
            <View style={styles.data}>
              <Text style={[styles.dataTitle, styles.text]}>EMS response required</Text>
              <Text style={[styles.dataValue, styles.text]}>{data.isEmsResponseRequired ? 'Required' : 'Not required'}</Text>
            </View>
            <View style={styles.data}>
              <Text wrap={false} style={[styles.dataTitle, styles.text]}>
                Fire department response required
              </Text>
              <Text style={[styles.dataValue, styles.text]}>{data.isFireDepartmentResponseRequired ? 'Required' : 'Not required'}</Text>
            </View>
            <View style={styles.data}>
              <Text wrap={false} style={[styles.dataTitle, styles.text]}>
                Police department response required
              </Text>
              <Text style={[styles.dataValue, styles.text]}>{data.isPoliceDepartmentResponseRequired ? 'Required' : 'Not required'}</Text>
            </View>
          </View>
          <View style={styles.descriptionWrapper}>
            <Text style={[styles.description, styles.text]}>{data.description}</Text>
          </View>
        </View>
        <View style={styles.delimiter}></View>

        <View style={styles.incidentLocation}>
          <Text style={styles.incidentLocationTitle}>People Involved</Text>
          <View style={styles.row} wrap={false}>
            <View style={styles.data}>
              <Text style={[styles.dataTitle, styles.text]}>Name</Text>
            </View>
            <View style={styles.data}>
              <Text style={[styles.dataTitle, styles.text]}>Address</Text>
            </View>
            <View style={styles.data}>
              <Text style={[styles.dataTitle, styles.text]}>Phone Number</Text>
            </View>
          </View>
          {data.involvedPersons.map((item, index) => {
            return (
              <View style={styles.row} wrap={false} key={`people-${index}`}>
                <View style={styles.data}>
                  <Text style={[styles.dataValue, styles.text]}>{`${item.firstName} ${item.lastName}`}</Text>
                </View>
                <View style={styles.data}>
                  <Text style={[styles.dataValue, styles.text]}>{`${item.streetNumber} ${item.streetName} ${
                    item.address ? item.address : ''
                  }`}</Text>
                </View>
                <View style={styles.data}>
                  <Text style={[styles.dataValue, styles.text]}>{formatPhone(item.phoneNumber)}</Text>
                </View>
              </View>
            );
          })}
        </View>

        <View style={styles.delimiter}></View>

        {!!data.involvedVehicles.length && (
          <>
            <View style={styles.incidentLocation}>
              <Text style={styles.incidentLocationTitle}>Involved Vehicles</Text>
              <Text style={[styles.involvedVehicles, styles.text]}>
                {data.involvedVehicles.map((item) => `${item.licenseNumber} (${item.licenseState})`).join(', ')}
              </Text>
            </View>

            <View style={styles.delimiter}></View>
          </>
        )}

        {!!data.attachmentsSrc.length && (
          <View style={styles.incidentLocation}>
            <View wrap={false}>
              <Text style={styles.incidentLocationTitle}>Photos of the incident</Text>
              <View style={styles.attachmentWrapper}>
                <Image src={data.attachmentsSrc[0]} style={styles.attachment} />
              </View>
            </View>
            {data.attachmentsSrc.slice(1).map((src, index) => {
              return (
                <View style={styles.attachmentWrapper}>
                  <Image key={`image-${index}`} src={src} style={styles.attachment} />
                </View>
              );
            })}
          </View>
        )}

        <Footer {...data} />
      </Page>
    );
  };

  return <Document>{props.reports.map((data, index) => getPage(data, index))}</Document>;
};

export default PrintIncidentReport;

const styles = StyleSheet.create({
  footer: { display: 'flex', alignItems: 'flex-end', position: 'absolute', bottom: 10, left: 20, right: 20, height: 40 },
  attachmentWrapper: { height: 320 },
  attachment: { height: '100%', width: '100%', objectFit: 'contain' },
  involvedVehicles: { fontSize: 14 },
  involvedVehiclesData: { width: 170 },
  descriptionWrapper: { marginTop: 10 },
  description: { fontSize: 12 },
  row: { display: 'flex', flexDirection: 'row', justifyContent: 'flex-start', gap: 10 },
  text: { color: '#292B2E' },
  data: { width: 180 },
  dataTitle: { fontSize: 12, fontWeight: 500, marginBottom: 5 },
  dataValue: { fontSize: 14, fontWeight: 400 },
  incidentLocationTitle: { marginBottom: 5 },
  incidentLocation: { marginTop: 20, display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 },
  delimiter: { borderBottom: '1pt solid #E3E5E8' },
  reportingOffcerData: { display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignContent: 'center', fontSize: 14 },
  reportingOffcer: { display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 20 },
  page: {
    paddingTop: 15,
    paddingHorizontal: 20,
    paddingBottom: 60,
    fontFamily: 'Work Sans',
    color: '#292B2E',
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignContent: 'center',
    marginBottom: 30,
  },
  logo: {
    width: 100,
  },
});
